'use client';

/**
 * Sign in / Create account — one card, two modes (owner 2026-10-12 redesign;
 * spec: docs/AUTH_REDDIT_GENDER_PROMPT.md).
 *
 * An account is a nickname, a password and a gender — nothing else is asked.
 * "Join with Reddit" sits on top: it goes through GET /api/auth/reddit/start,
 * and a new Reddit account picks its gender on /welcome afterwards.
 *
 * The look is the Settings dialog's (components/settingsUi.ts): neutral ink,
 * one solid black button, no accent colour. The only colour on the card is
 * Reddit's own mark.
 *
 * Kept from the old form, deliberately:
 *   · the saved nickname + password are pre-filled on the sign-in page
 *     (lib/auth.tsx replays them automatically when the cookie is gone);
 *   · a signed-in visitor is sent straight on to the translator;
 *   · the button is never disabled for a missing answer — pressing it says
 *     what is missing, next to the box it is about.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import BrandMark from './BrandMark';
import { BRAND } from '@/lib/brand';
import { useAuth, rememberedNickname } from '@/lib/auth';
import { useWait } from '@/lib/wait';
import type { Gender } from '@/lib/api';
import { S_BTN, S_BTN_PRIMARY, S_INPUT, S_MUTED } from './settingsUi';

/** Where to go after signing in: a same-site `?next=/path` (Camps sends one), else the translator. */
function returnPath(): string {
  if (typeof window === 'undefined') return '/';
  const next = new URLSearchParams(window.location.search).get('next') ?? '';
  return /^\/(?![/\\])[^\s]*$/.test(next) && !next.startsWith('/login') && !next.startsWith('/register') ? next : '/';
}

const REDDIT_ERRORS: Record<string, string> = {
  'reddit-off': 'Reddit sign-in is not set up on this server yet. Use a nickname and password for now.',
  'reddit-failed': 'Reddit sign-in did not go through. Please try again.',
  'reddit-cancelled': 'Reddit sign-in was cancelled.',
  'reddit-disabled': 'This account has been disabled.',
};

function Note({ children, bad }: { children: React.ReactNode; bad?: boolean }) {
  return (
    <p role={bad ? 'alert' : undefined}
       className={'mt-1.5 text-xs ' + (bad ? 'text-red-600 dark:text-red-400' : S_MUTED)}>
      {children}
    </p>
  );
}

export default function AuthForm({ mode }: { mode: 'login' | 'register' }) {
  const router = useRouter();
  const auth = useAuth();
  const register = mode === 'register';

  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [gender, setGender] = useState<Gender | null>(null);
  const remember = true;   // one kind of session — see backend lib/auth.ts
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  const nickBox = useRef<HTMLInputElement | null>(null);
  const passBox = useRef<HTMLInputElement | null>(null);
  const genderBox = useRef<HTMLFieldSetElement | null>(null);

  // The last nickname used here, pre-filled (sign-in page only). Never a password: none is stored.
  useEffect(() => {
    if (register) return;
    const nick = rememberedNickname();
    if (nick) setNickname(nick);
  }, [register]);

  // A Reddit round trip that came back with an error.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('error');
    if (code && REDDIT_ERRORS[code]) setError(REDDIT_ERRORS[code]);
  }, []);

  useEffect(() => { nickBox.current?.focus(); }, []);

  // The request is in flight, or it worked and the translator is on its way:
  // either way the form is finished with, and the wait says what happens next.
  useWait(busy, register ? 'Creating your account' : 'Signing you in');
  useWait(auth.loaded && !!auth.user, 'Opening the translator');

  useEffect(() => {
    if (auth.loaded && auth.user) router.replace(returnPath());
  }, [auth.loaded, auth.user, router]);

  const short = register ? auth.minPassword - password.length : 0;
  const nickProblem = !nickname.trim() ? (register ? 'Choose a nickname.' : 'Type your nickname.') : null;
  const passProblem = !password
    ? (register ? `Choose a password — at least ${auth.minPassword} characters.` : 'Type your password.')
    : register && short > 0 ? `${short} more character${short === 1 ? '' : 's'} — at least ${auth.minPassword}.` : null;
  const genderProblem = register && !gender ? 'Please select your gender to continue.' : null;
  const ready = !nickProblem && !passProblem && !genderProblem;

  const submit = useCallback(async () => {
    if (busy) return;
    if (!ready) {
      setTried(true); setError(null);
      if (nickProblem) nickBox.current?.focus();
      else if (passProblem) passBox.current?.focus();
      // Gender has no text field to focus, so bring it into view instead — a
      // silent, blocked "Create account" otherwise looks like nothing happened.
      else if (genderProblem) genderBox.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    setBusy(true); setError(null);
    try {
      if (register) await auth.signUp(nickname.trim(), password, gender as Gender, remember);
      else await auth.signIn(nickname.trim(), password, remember);
      router.replace(returnPath());
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }, [busy, ready, nickProblem, passProblem, genderProblem, register, auth, nickname, password, gender, remember, router]);

  return (
    <main className="flex min-h-screen w-full items-center justify-center bg-ink-100/70 px-4 py-10 dark:bg-ink-950">
      <div className="w-full max-w-[400px]">
        <Link href="/" className="mx-auto mb-7 flex w-fit items-center gap-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-400 rounded-lg">
          <BrandMark className="h-8 w-8 rounded-lg" />
          <span className="text-lg font-bold tracking-tight text-ink-900 dark:text-white">
            {BRAND.name}
          </span>
        </Link>

        <div className="rounded-2xl border border-ink-200 bg-white p-7 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.35)]
                        dark:border-white/10 dark:bg-ink-900">
          <h1 className="text-xl font-semibold tracking-tight text-ink-900 dark:text-white">
            {register ? 'Create your account' : 'Welcome back'}
          </h1>
          <p className={'mt-1 text-sm ' + S_MUTED}>
            {register ? `Join ${BRAND.name} in a few seconds.` : `Sign in to ${BRAND.name}.`}
          </p>

          <a href="/api/auth/reddit/start?next=/" className={S_BTN + ' mt-6 h-10 w-full'}>
            <RedditMark className="h-5 w-5" />
            {register ? 'Join with Reddit' : 'Continue with Reddit'}
          </a>

          <div className="my-5 flex items-center gap-3 text-xs text-ink-400 dark:text-ink-500" aria-hidden>
            <span className="h-px flex-1 bg-ink-200 dark:bg-white/10" />
            or with a nickname
            <span className="h-px flex-1 bg-ink-200 dark:bg-white/10" />
          </div>

          <form noValidate onSubmit={(e) => { e.preventDefault(); void submit(); }} className="grid gap-4">
            <div>
              <label htmlFor="auth-nick" className="mb-1.5 block text-sm font-medium text-ink-800 dark:text-ink-dim">Nickname</label>
              <input
                id="auth-nick"
                ref={nickBox}
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                maxLength={auth.maxNickname}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={tried && !!nickProblem}
                className={S_INPUT}
              />
              {tried && nickProblem && <Note bad>{nickProblem}</Note>}
            </div>

            <div>
              <label htmlFor="auth-pass" className="mb-1.5 block text-sm font-medium text-ink-800 dark:text-ink-dim">Password</label>
              <div className="relative">
                <input
                  id="auth-pass"
                  ref={passBox}
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={register ? 'new-password' : 'current-password'}
                  aria-invalid={tried && !!passProblem}
                  className={S_INPUT + ' pr-16'}
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  aria-label={show ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-ink-500 hover:text-ink-900
                             focus:outline-none focus-visible:underline dark:text-ink-400 dark:hover:text-white"
                >
                  {show ? 'Hide' : 'Show'}
                </button>
              </div>
              {register
                ? (passProblem ? <Note bad={tried}>{passProblem}</Note> : <Note>Looks good.</Note>)
                : tried && passProblem && <Note bad>{passProblem}</Note>}
            </div>

            {register && (
              <fieldset ref={genderBox}>
                <legend className="mb-1.5 block text-sm font-medium text-ink-800 dark:text-ink-dim">
                  Gender <span className="text-red-500" aria-hidden>*</span>
                  <span className="sr-only">(required)</span>
                </legend>
                <GenderPicker value={gender} onChange={setGender} invalid={tried && !!genderProblem} />
                {tried && genderProblem && <Note bad>{genderProblem}</Note>}
              </fieldset>
            )}

            {/* There was a "Keep me signed in" box here. Staying signed in is
                now the rule rather than a choice (owner, 2026-10-13), so the box
                is gone and this line says what happens instead — on a shared
                computer the way out is Sign out, and people should be told so. */}
            <p className="text-xs text-ink-400 dark:text-ink-500">
              You stay signed in on this browser until you sign out.
            </p>

            {error && (
              <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700
                                         dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className={S_BTN_PRIMARY + ' h-10 w-full'}>
              {busy ? (register ? 'Creating your account…' : 'Signing in…') : (register ? 'Create account' : 'Sign in')}
            </button>
          </form>
        </div>

        <p className={'mt-6 text-center text-sm ' + S_MUTED}>
          {register ? 'Already have an account? ' : 'New here? '}
          <Link href={register ? '/login' : '/register'}
                className="font-medium text-ink-900 underline-offset-4 hover:underline dark:text-white">
            {register ? 'Sign in' : 'Create an account'}
          </Link>
        </p>
      </div>
    </main>
  );
}

/** Male / Female as one segmented control — shared with /welcome and Settings ▸ Profile. */
export function GenderPicker({ value, onChange, invalid = false }: { value: Gender | null; onChange: (g: Gender) => void; invalid?: boolean }) {
  const opts: { id: Gender; label: string }[] = [{ id: 'male', label: 'Male' }, { id: 'female', label: 'Female' }];
  return (
    <div role="radiogroup" aria-label="Gender" aria-invalid={invalid || undefined}
         className={
           'grid grid-cols-2 gap-1 rounded-lg border p-1 ' +
           (invalid
             ? 'border-red-400 ring-1 ring-red-300 dark:border-red-500/70 dark:ring-red-500/30'
             : 'border-ink-200 dark:border-white/10')
         }>
      {opts.map((o) => {
        const on = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.id)}
            className={
              'h-8 rounded-md text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-400 ' +
              (on ? 'bg-ink-900 text-white dark:bg-ink-dim dark:text-ink-950'
                  : 'text-ink-600 hover:bg-ink-100 dark:text-ink-300 dark:hover:bg-white/5')
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Reddit's mark — the one coloured thing on the card. */
export function RedditMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden>
      <circle cx="10" cy="10" r="10" fill="#FF4500" />
      <ellipse cx="10" cy="11.6" rx="5.4" ry="3.6" fill="#fff" />
      <circle cx="14.6" cy="8.3" r="1.25" fill="#fff" />
      <circle cx="5.4" cy="8.3" r="1.25" fill="#fff" />
      <circle cx="13.6" cy="4.4" r="1.05" fill="#fff" />
      <path d="M10 8 L10.8 4.1 L13.6 4.4" stroke="#fff" strokeWidth=".8" fill="none" strokeLinecap="round" />
      <circle cx="8" cy="11.1" r=".85" fill="#FF4500" />
      <circle cx="12" cy="11.1" r=".85" fill="#FF4500" />
      <path d="M7.9 13.3c1.2.8 3 .8 4.2 0" stroke="#FF4500" strokeWidth=".75" fill="none" strokeLinecap="round" />
    </svg>
  );
}
